import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { fetchUserDetails } from "@/actions/userprofile";
import { getUserId } from "@/helpers/common";
import {
  getGitchaProOffer,
  keepGitchaPro,
  startGitchaProTrial,
} from "@/services/gitchaProService";

// Per user: the offer is read through the shared query client, which outlives
// a logout that does not reload the page.
export const gitchaProOfferKey = () => ["gitchaProOffer", getUserId()];

const settle = async (request) => {
  try {
    return await request();
  } catch (error) {
    if (error?.response?.data) return error.response.data;
    throw error;
  }
};

const resultOf = (response, fallback) => {
  if (response?.errorCode !== 0) {
    const error = new Error(response?.message || fallback);
    error.offer = response?.data?.offer || null;
    throw error;
  }
  return response.result;
};

export const useGitchaProOffer = (enabled = true) =>
  useQuery({
    queryKey: gitchaProOfferKey(),
    queryFn: async () =>
      resultOf(await settle(getGitchaProOffer), "Could not load Gitcha Pro."),
    enabled,
    staleTime: 0,
  });

// Plan state lives in three places: the offer, the userDetails query, and
// Redux. Every plan change has to refresh all of them.
export const useRefreshGitchaPro = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  return (offer) => {
    if (offer) queryClient.setQueryData(gitchaProOfferKey(), offer);
    else queryClient.invalidateQueries({ queryKey: gitchaProOfferKey() });
    queryClient.invalidateQueries({ queryKey: ["userDetails"] });
    const userId = getUserId();
    if (userId) dispatch(fetchUserDetails({ userId }));
  };
};

const usePlanChange = (mutationFn, fallback, options) => {
  const queryClient = useQueryClient();
  const refresh = useRefreshGitchaPro();
  return useMutation({
    mutationFn: async (input) =>
      resultOf(await settle(() => mutationFn(input)), fallback),
    onSuccess: (offer, ...rest) => {
      refresh(offer);
      options.onSuccess && options.onSuccess(offer, ...rest);
    },
    onError: (error, ...rest) => {
      if (error.offer) queryClient.setQueryData(gitchaProOfferKey(), error.offer);
      options.onError && options.onError(error, ...rest);
    },
  });
};

export const useStartGitchaProTrial = (options = {}) =>
  usePlanChange(
    () => startGitchaProTrial(),
    "Could not start your trial. Please try again.",
    options,
  );

export const useKeepGitchaPro = (options = {}) =>
  usePlanChange(
    (paymentModeId) => keepGitchaPro(paymentModeId),
    "Could not set up billing. Please try again.",
    options,
  );
